// Maturity Roadmap page copy, word for word from the DIGU Site Reference artifact.
import { ROUTES } from './routes';

export const ROADMAP = {
  hero: {
    eyebrow: 'Secure AI Adoption Maturity Roadmap',
    title: 'Where does your organization actually stand?',
    lede: 'Five levels, scored separately across five control domains, so you get a profile rather than a grade. Each level is described by what it lets you safely do next, not by how locked down you are.',
  },

  levels: {
    eyebrow: 'The levels',
    title: 'Control buys autonomy',
    intro:
      'Each level adds controls, and each set of controls lets you safely do more. Find where you are today, then find where you need to be.',
    unlocksLabel: 'Unlocks',
    // Pieces of the per-row value label and the bar tooltip, e.g.
    // "of 32 controls", "+ 5 practices",
    // "Level 4, Scaled: 32 of 32 controls in place plus 5 practices beyond the baseline".
    labels: {
      ofControls: (total) => `of ${total} controls`,
      plusPractices: (n) => `+ ${n} practices`,
      tip: (lv, total) =>
        `${lv.n}, ${lv.name}: ${lv.controls} of ${total} controls in place` +
        (lv.practices ? ` plus ${lv.practices} practices beyond the baseline` : ''),
    },
    items: [
      {
        n: 'Level 0',
        name: 'Ad hoc',
        tag: 'Where most teams start',
        controls: 0,
        truth: 'Everyone configures their own setup. No policy, no visibility.',
        unlocks: 'Nothing. The most risk for the least return.',
      },
      {
        n: 'Level 1',
        name: 'Contained',
        controls: 8,
        truth: 'Settings enforced, destructive actions denied, secrets out of reach, agent identity read only.',
        unlocks: 'Your developers can use AI on code, and your security team can live with it.',
      },
      {
        n: 'Level 2',
        name: 'Governed',
        tag: 'Target for most software companies',
        controls: 20,
        truth: 'Trust boundary written down, connections allowlisted, changes approved, policy and runbook in place.',
        unlocks: 'Connecting AI to your internal systems.',
      },
      {
        n: 'Level 3',
        name: 'Assured',
        tag: 'Target for regulated organizations',
        tagKey: true,
        controls: 32,
        truth: 'Agent activity visible and attributable, evidence maintained, controls reviewed, drills run.',
        unlocks: 'Audit confidence, regulated workloads, and the evidence a customer security review asks for.',
      },
      {
        n: 'Level 4',
        name: 'Scaled',
        tag: 'Only where agents run infrastructure',
        controls: 32,
        practices: 5,
        truth: 'Autonomy tiered by risk, control testing automated, red teaming scheduled.',
        unlocks: 'Agents provisioning infrastructure and running in your pipelines.',
      },
    ],
    legend: {
      controls: 'Controls in place, of the 32 in the baseline',
      practices: 'Level 4 practices, beyond the baseline',
    },
    footnote:
      'Level 4 is not more controls. It adds five practices on top of all thirty two, such as automated control testing and scheduled red teaming, and it is only worth reaching where agents run infrastructure or pipelines.',
  },

  domains: {
    eyebrow: 'The five domains',
    title: 'Scored separately, because nobody is uniformly weak',
  },

  scoring: {
    eyebrow: 'Scoring',
    title: 'Scored on evidence, not opinion',
    paragraphs: [
      'A control counts when the evidence exists. "We think we do that" scores zero. We report two numbers: the average across the domains, and the floor, which is your lowest domain. Risk follows the floor, so that is the number to act on.',
      "Our model maps to the Cloud Security Alliance's agentic governance work and to OWASP's agentic risk list. You can score yourself against those for free. What we add is evidence, implementation, and someone to keep it current.",
    ],
  },

  targets: {
    eyebrow: 'Targets',
    title: 'Level 3 is not the answer for everyone',
    columns: ['Your situation', 'Twelve month target'],
    rows: [
      [
        'Under 50 engineers, no regulated data',
        'Level 2 across the board. Assurance machinery costs more than it returns at your size.',
      ],
      [
        '50 to 300 engineers',
        'Level 2, with level 3 on observability and identity, so you can answer customer security reviews.',
      ],
      [
        'Regulated, or holding SOC 2 or ISO 27001',
        'Level 3 across the board. Your auditor will ask, and the evidence pack is the answer.',
      ],
      ['Agents touching infrastructure or pipelines', 'Level 3, plus level 4 in identity and permissions.'],
    ],
    // Numbered per website feedback: "Two rules of thumb", then 1 and 2.
    rules: {
      lead: 'Two rules of thumb.',
      items: [
        'Never leave a domain at 0 once Claude has production access.',
        'Move one level at a time, because a two level jump in a quarter usually means the controls went in without the adoption.',
      ],
    },
    actions: [{ label: 'Score yourself in ten questions', to: ROUTES.score }],
  },
};
